import * as DOM from '../dom/dom.js';  // DOM manipulation

/**
 * Window.alert replacement in Promise() mode
 * @param    {string}  message        A string of text to display to the user.
 * @param    {string}  [confirmText]  Optional. Text for "Confirm" button. Default value: 'Ok'.
 * @returns  {Promise}                A Promise that resolves to the modal window.
 */
window.asyncAlert = function (message = '', confirmText = 'Ok')
{
	if (!message) return Promise.resolve();                                        // No message? Simply resolve
	else return _createAsyncModal('alert', message, confirmText);                  // Create modal window - returns Promise
}

/**
 * Window.confirm replacement in Promise() mode
 * @param    {string}  message        A string of text to display to the user.
 * @param    {string}  [confirmText]  Optional. Text for "Confirm" button. Default value: 'Ok'.
 * @param    {string}  [cancelText]   Optional. Text for "Cancel" button. Default value: 'Cancel'.
 * @returns  {Promise}                A Promise that resolves to the modal window.
 */
window.asyncConfirm = function (message = '', confirmText = 'Ok', cancelText = 'Cancel')
{
	if (!message) return Promise.resolve();                                                        // No message? Simply return
	else return _createAsyncModal('confirm', message, confirmText, cancelText);                    // Create modal window - returns Promise
}

/**
 * Window.prompt replacement in Promise() mode
 * @param    {string}  message         A string of text to display to the user.
 * @param    {string}  [defaultValue]  Optional. A string containing the default value displayed in the text input field. Default value: none.
 * @param    {string}  [confirmText]   Optional. Text for "Confirm" button. Default value: 'Ok'.
 * @param    {string}  [cancelText]    Optional. Text for "Cancel" button. Default value: 'Cancel'.
 * @returns  {Promise}                 A Promise that resolves to the modal window.
 */
window.asyncPrompt = function (message = '', defaultValue = '', confirmText = 'Ok', cancelText = 'Cancel')
{
	if (!message) return Promise.resolve();                                                                    // No message? Simply return
	else return _createAsyncModal('prompt', message, confirmText, cancelText, defaultValue);                   // Create modal window - returns Promise
}

/**
 * Listen if the "Escape" key being pressed and closes the last opened modal.
 * @param  {KeyboardEvent}  event  The keyboard event object.
 */
window.addEventListener('keyup', event =>
{
	// Check if the "Escape" key was pressed
	var isEscape = false;
	if (event.key && (event.key == 'Escape')) isEscape = true;  // Modern browsers
	if (event.keyCode && (event.keyCode == 27)) isEscape = true;  // Old browsers

	// If the "Escape" key was pressed
	if (isEscape)
	{
		// Get and close the last opened modal
		let modals = document.querySelectorAll('.asyncModal');
		if (modals.length && modals[modals.length - 1].cancel) modals[modals.length - 1].cancel('Escape');
	}
});

/**
 * Creates a modal window for displaying a message and getting user input.
 * @param    {string}   type            The type of modal window to create. One of "alert", "confirm", "prompt".
 * @param    {string}   message         The message to display in the modal window.
 * @param    {string}   confirmText     The text to display on the "Confirm" button.
 * @param    {string}   [cancelText]    Optional. The text to display on the "Cancel" button. Only used if type != 'alert'.
 * @param    {string}   [defaultValue]  Optional The default value to display in the text input field. Only used if type == 'prompt'.
 * @returns  {Promise}                  A Promise that resolves to the modal window.
 */
function _createAsyncModal(type, message, confirmText, cancelText = null, defaultValue = null)
{
	return new Promise((resolve, reject) =>
	{
		// Preset the focus element
		var focusElement = null;

		// Create main structure: modal (hidden while creating) and content elements
		var modal = DOM.create('div', { class: 'asyncModal async' + type.ucfirst() }, { display: 'none' });
		var content = DOM.create('form', { class: 'asyncContent' });
		modal.appendChild(content);

		// Create message paragraph
		let textMessage = DOM.create('p', { class: 'asyncMessage' });
		message = message.replaceAll(/\\n/, "\n");          // Convert \\n into \n
		message = message.replaceAll(/<br[^>]*>\n/, "\n");  // Remove <br>, if there
		message = message.replaceAll("\n", "<br />\n");     // Put <br> for each \n
		textMessage.innerHTML = message;
		content.appendChild(textMessage);  // Append to content

		// Only "prompt" modal has text input
		if (type == 'prompt')
		{
			// Create text <input> and <fieldset>
			let textFieldset = DOM.create('fieldset', { class: 'asyncFieldset asyncInputFieldset' });
			let textInput = DOM.create('input', { type: 'text', class: 'asyncInput' }, {}, defaultValue);
			textFieldset.appendChild(textInput);
			content.appendChild(textFieldset);  // Append to content
			if (!focusElement) focusElement = textInput;  // New focus element #1
		}

		// Create buttons <fieldset>
		let buttonsFieldset = DOM.create('fieldset', { class: 'asyncFieldset asyncButtonsFieldset' });
		content.appendChild(buttonsFieldset);

		// Create confirm <button>
		let confirmButton = DOM.create('input', { type: 'button', class: 'asyncButton asyncConfirm' }, {}, confirmText);
		buttonsFieldset.appendChild(confirmButton);
		if (!focusElement) focusElement = confirmButton;  // New focus element #2

		// Add click handler to confirm <button>
		confirmButton.addEventListener('click', () =>
		{
			// Get result, if any
			let result = null;
			if (type == 'prompt') result = modal.querySelector('.asyncInput').value;

			// Return
			resolve(result);  // Return "true" or the input value
			modal.remove();   // Remove modal window
		});

		// Non-"alert" modals has cancel button
		if (type != 'alert')
		{
			// Create cancel <button>
			let cancelButton = DOM.create('input', { type: 'button', class: 'asyncButton asyncCancel' }, {}, cancelText);
			buttonsFieldset.appendChild(cancelButton);

			// Add click handler to cancel <button>
			cancelButton.addEventListener('click', () =>
			{
				reject();        // Return "false"
				modal.remove();  // Remove modal window
			});
		}

		// Add submit handler to content <form>
		content.addEventListener('submit', e =>
		{
			// Prevent default action
			e.preventDefault();

			// Emulate click on confirm button
			confirmButton.click();
		});

		// Append a "cancel" method to the modal window
		modal.cancel = (mode = null) =>
		{
			// How do we get here?
			if ((mode == 'Escape') && (type == 'alert')) resolve();  // Pressing "Escape" on an alert must return "true"
			else reject();  // Return "false" otherwise

			// Remove modal window
			modal.remove();
		};

		// Append modal window to body
		document.body.append(modal);

		// A little trick
		setTimeout(() =>
		{
			// Check for modal style
			_checkAsyncStyle(modal);

			// Set the focus element
			if (focusElement) focusElement.focus();
		}, 1);
	});
}

/**
 * Set styles to all elements if a style does not exists.
 * @param  {HTMLElement}  modal  The modal window DOM element.
 */
function _checkAsyncStyle(modal)
{
	// Check if modal position is set to "fixed" (that means a style do exists)
	if (getComputedStyle(modal).position != 'fixed')
	{
		// Set styles to all elements
		DOM.setStyles(modal, { boxSizing: 'border-box', position: 'fixed', top: '0', left: '0', width: '100%', backgroundColor: '#000000AA', height: '100%', zIndex: '9999' });  // Set styles to wrap
		DOM.setStyles(modal.querySelector('.asyncContent'), { boxSizing: 'border-box', display: 'block', padding: '1em', backgroundColor: '#990066', boxShadow: '0 0 3px 0 #990066', color: '#DDDDDD', margin: '30vh auto 0 auto', width: '33%', minWidth: '320px' });  // Set styles to content
		DOM.setStyles(modal.querySelector('.asyncMessage'), { boxSizing: 'border-box', padding: 0, margin: 0 });  // Set styles to message
		DOM.setStyles(modal.querySelector('.asyncInputFieldset'), { boxSizing: 'border-box', display: 'flex', justifyContent: 'flex-end', border: 'none', padding: 0, margin: '1em 0 0 0' });  // Set styles to text <fieldset>
		DOM.setStyles(modal.querySelector('.asyncInput'), { boxSizing: 'border-box', width: '100%' });  // Set styles to text <input>
		DOM.setStyles(modal.querySelector('.asyncButtonsFieldset'), { boxSizing: 'border-box', display: 'flex', justifyContent: 'flex-end', border: 'none', padding: 0, margin: '1em 0 0 0' });  // Set styles to buttons <fieldset>
		DOM.setStyles(modal.querySelector('.asyncConfirm'), { boxSizing: 'border-box', marginLeft: '1em', cursor: 'pointer' });  // Set styles to confirm <button>
		DOM.setStyles(modal.querySelector('.asyncCancel'), { boxSizing: 'border-box', marginLeft: '1em', cursor: 'pointer' });  // Set styles to cancel <button>
	}

	// Remove inline display=hidden attribute
	DOM.removeStyles(modal, ['display']);
}